import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "../lib/utils"; // Utility function for class names
import { Button } from "../@/components/ui/button"; // Custom Button component
import { Calendar } from "../@/components/ui/calendar"; // Custom Calendar component
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../@/components/ui/popover"; // Custom Popover components

interface DateSinglePickerProps {
  text: string;
  onDateSelect: (date: Date | undefined) => void; // Callback for selected date
  className?: string; // Optional className for styling
  startDate?: Date; // Optional start date for comparison
  endDateError?: boolean; // Error flag for end date validation
}

export function DateSinglePicker({
  text,
  onDateSelect,
  className,
  startDate,
  endDateError = false, // Default to no error
}: DateSinglePickerProps) {
  const [date, setDate] = React.useState<Date | undefined>(undefined);
  const [borderColor, setBorderColor] = React.useState<string>("");

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (startDate && selectedDate && selectedDate < startDate) {
      // If selected date is before start date, show error
      setBorderColor("border-red-500");
    } else {
      setBorderColor(""); // Reset border color if valid date
    }
    setDate(selectedDate);
    onDateSelect(selectedDate); // Call the callback with the selected date
  };

  const handleInvalidDate = (date: Date) => {
    if (date <= new Date()) {
      setBorderColor("border-red-500"); // Red border for invalid dates
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "h-9 justify-start text-left font-normal gap-2",
            !date && "text-muted-foreground",
            className,
            borderColor // Apply red border if date is invalid
          )}
        >
          {/* Ensure the icon size is consistent */}
          <CalendarIcon className="w-4 h-4" />
          {/* Show selected date or placeholder */}
          {date ? format(date, "PPP") : <span>{text}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateSelect} // Use the new handler
          initialFocus
          disabled={(date) => (startDate ? date < startDate : false)} // Disable dates before start date
        />
      </PopoverContent>
      {endDateError && (
        <div className="text-red-500 text-sm mt-2">
          End date cannot be earlier than start date.
        </div>
      )}
    </Popover>
  );
}
